﻿(() => {
  let _debounce = null;
  let _isRemoving = false;
  let _recalculating = false;

  const _tpl = (str, vars) =>
    Object.entries(vars).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, v), str ?? '');

  const _str = window.theme?.shopSettings?.testerGift?.strings ?? {};

  const getThresholds = () => {
    const bar = document.getElementById('tester-progress-bar');
    if (bar?.dataset.thresholdFs) {
      const symbol = bar.dataset.currencySymbol || window.theme?.shopSettings?.currencySymbol || '€';
      return {
        freeShipping: parseInt(bar.dataset.thresholdFs, 10),
        tier1:        parseInt(bar.dataset.thresholdT1, 10),
        tier2:        parseInt(bar.dataset.thresholdT2, 10),
        isUK:         symbol === '£',
        symbol,
      };
    }
    const s   = window.theme?.shopSettings;
    const iso = s?.isoCode ?? '';
    const isUK = iso === 'GBP';
    const isEU = iso === 'EUR';
    const t   = s?.testerGift ?? {};
    const cfg = t.config ?? {};
    const mkt = isUK ? (cfg.uk ?? {}) : isEU ? (cfg.eu ?? {}) : (cfg.currencies?.[iso] ?? cfg.eu ?? {});
    return {
      freeShipping: isUK ? (t.freeShippingUK ?? 12000) : isEU ? (t.freeShippingEU ?? 9000) : 0,
      tier1:        t.tier1 ?? (mkt.tier1 ?? (isUK ? 150 : 170)) * 100,
      tier2:        t.tier2 ?? (mkt.tier2 ?? (isUK ? 190 : 210)) * 100,
      isUK,
      symbol: s?.currencySymbol ?? (isUK ? '£' : '€'),
    };
  };

  const _tagCache = {};

  const getTagMap = () => {
    const el = document.getElementById('cart-product-tags');
    if (el) {
      try { Object.assign(_tagCache, JSON.parse(el.textContent)); } catch { /* silent */ }
    }
    return _tagCache;
  };

  const calcQualifyingSubtotal = (items, tagMap) =>
    items.reduce((sum, item) => {
      const tags = tagMap[item.product_id] ?? [];
      if (!tags.includes('full-size') || tags.includes('travel-set')) return sum;
      return sum + item.final_line_price;
    }, 0);

  const calcShippingSubtotal = (items, tagMap) =>
    items.reduce((sum, item) => {
      const tags = tagMap[item.product_id] ?? [];
      if (tags.includes('tester-gift')) return sum;
      return sum + item.final_line_price;
    }, 0);

  const calcTier = (subtotal, { tier1, tier2 }) => {
    if (subtotal >= tier2) return 2;
    if (subtotal >= tier1) return 1;
    return 0;
  };

  const hasNoQualifyingItems = (items, tagMap) =>
    items.length > 0 && items.every(item => {
      const tags = tagMap[item.product_id] ?? [];
      return tags.includes('tester-gift');
    });

  const updateProgressBar = (subtotal, thresholds, tier, testerCount, phase) => {
    const bars = document.querySelectorAll('.cart-miniature-bar');
    if (!bars.length) return;

    const inTesterPhase = phase === 'tester';
    if (inTesterPhase) {
      const drActive = document.documentElement.classList.contains('dr-active');
      const drHidden = bars[0]?.dataset.drHidden === 'true';
      if (drActive || drHidden) {
        bars.forEach(el => { el.style.display = 'none'; });
        return;
      }
    }
    bars.forEach(el => { el.style.display = ''; });

    const msgs   = document.querySelectorAll('.cart-miniature-bar__message');
    const tracks = document.querySelectorAll('.cart-miniature-bar__track');
    if (!msgs.length && !tracks.length) return;

    const alreadyCurrent = [...bars].every(
      el => parseInt(el.dataset.barSubtotal, 10) === subtotal && el.dataset.phase === phase
    ) && [...msgs].every(el => el.textContent.trim().length > 0);
    if (alreadyCurrent) return;

    bars.forEach(el => {
      el.dataset.barSubtotal = subtotal;
      el.dataset.phase = phase;
    });

    const { symbol, tier1, tier2, freeShipping } = thresholds;
    let pct, text;

    if (phase === 'shipping') {
      pct  = Math.min(subtotal / freeShipping, 1);
      const diff = Math.ceil((freeShipping - subtotal) / 100);
      text = _tpl(_str.progressShippingPhase, { symbol, amount: diff });
    } else if (tier === 0) {
      pct  = Math.min((subtotal - freeShipping) / (tier1 - freeShipping), 1);
      const diff = ((tier1 - subtotal) / 100).toFixed(2);
      text = _tpl(_str.progressTier0, { symbol, amount: diff });
    } else if (tier === 1) {
      pct  = Math.min((subtotal - tier1) / (tier2 - tier1), 1);
      const diff = ((tier2 - subtotal) / 100).toFixed(2);
      text = testerCount === 1
        ? _tpl(_str.progressTier1TesterOne, { symbol, amount: diff })
        : _tpl(_str.progressTier1ChooseOne, { symbol, amount: diff });
    } else {
      pct  = 1;
      if (testerCount >= 2)       text = _str.progressTier2TwoIncluded;
      else if (testerCount === 1) text = _str.progressTier2OneIncluded;
      else                        text = _str.progressTier2ChooseTwo;
    }

    const widthVal = `${(pct * 100).toFixed(2)}%`;
    tracks.forEach(track => {
      track.innerHTML = `<div class="cart-miniature-bar__fill" style="width:${widthVal}">${widthVal}</div>`;
    });
    if (text) msgs.forEach(el => { el.textContent = text; });
  };

  const updateShippingStatus = (subtotal, thresholds) => {
    const statusEls = document.querySelectorAll('.cart-shipping-status');
    if (!statusEls.length) return;

    const { freeShipping, symbol, isUK } = thresholds;

    if (freeShipping === 0) {
      statusEls.forEach(el => { el.style.display = 'none'; });
      return;
    }

    statusEls.forEach(el => {
      if (parseInt(el.dataset.subtotal, 10) === subtotal) return;
      el.dataset.subtotal = subtotal;
      el.style.display = '';

      const textEl = el.querySelector('.cart-shipping-status__text');
      if (!textEl) return;

      if (!_str.shippingFree) return;
      if (subtotal >= freeShipping) {
        const label = isUK ? _str.shippingUkUnlocked : _str.shippingStandardUnlocked;
        textEl.innerHTML = `<span class="cart-shipping-status__bold">${_str.shippingFree}</span><span class="cart-shipping-status__regular"> ${label}</span>`;
      } else {
        const diff = ((freeShipping - subtotal) / 100).toFixed(2);
        textEl.innerHTML = `<span class="cart-shipping-status__regular">${_str.shippingAddPrefix} </span><span class="cart-shipping-status__bold">${symbol}${diff}</span><span class="cart-shipping-status__regular"> ${_str.shippingAddSuffix}</span>`;
      }
    });
  };

  const updateSelectButtons = (tier, testerCount, maxAllowed, phase, noQualifying) => {
    if (document.documentElement.classList.contains('dr-active')) return;

    const html       = document.documentElement;
    const hideSelect = phase === 'shipping' || noQualifying;

    html.classList.toggle('tg-hide-select',   hideSelect);
    html.classList.toggle('tg-no-qualifying', noQualifying);

    if (hideSelect) return;

    const cmBars  = document.querySelectorAll('.cm-bar');
    const trigger = document.getElementById('miniature-select-trigger');
    const barBtn  = document.querySelector('.cm-bar__btn[data-modal-trigger]');
    const { symbol, tier1 } = getThresholds();

    const locked    = tier === 0;
    const allFilled = !locked && testerCount >= maxAllowed;
    const state     = locked ? 'locked-tester' : 'unlocked';

    // guard: skip if .cm-bar already has correct state from Liquid SSR or prior JS run
    const barAlreadyCurrent = cmBars.length > 0 && [...cmBars].every(
      el => el.dataset.state === state &&
            parseInt(el.dataset.testerCount, 10) === testerCount &&
            parseInt(el.dataset.tier, 10) === maxAllowed
    );
    if (!barAlreadyCurrent) {
      cmBars.forEach(el => {
        el.dataset.state       = state;
        el.dataset.testerCount = testerCount;
        el.dataset.tier        = maxAllowed;
        el.setAttribute('aria-disabled', String(locked));
      });

      if (barBtn) {
        barBtn.disabled = locked;
        const btnTxt = locked    ? _tpl(_str.barUnlockAt, { symbol, amount: tier1 / 100 })
                     : allFilled ? _str.barChangeBtn
                     :             _str.barSelectBtn;
        if (btnTxt) barBtn.textContent = btnTxt;
      }

      document.querySelectorAll('.cm-bar__title').forEach(el => {
        const textNode = el.firstChild;
        if (textNode && textNode.nodeType === Node.TEXT_NODE) {
          const titleTxt = allFilled ? _str.barChangeTitle : _str.barChooseTitle;
          if (titleTxt) textNode.textContent = titleTxt;
        }
      });
    }

    // trigger button (mini-cart) — always sync disabled state
    if (trigger) {
      trigger.dataset.state = state;
      trigger.disabled = locked;
      trigger.setAttribute('aria-disabled', String(locked));
      const textEl = trigger.querySelector('.cart-miniature-select__text');
      if (textEl) {
        const trigTxt = allFilled ? _str.barChangeTrigger : _str.barChooseTrigger;
        if (trigTxt) textEl.textContent = trigTxt;
      }
    }
  };

  const updateModal = (tier) => {
    const modal = document.getElementById('miniature-select-modal');
    if (!modal) return;
    modal.dataset.maxTesters = tier;

    if (tier < 2) {
      modal.querySelectorAll('.msm__radio:checked')
        .forEach((el, i) => { if (i >= tier) el.checked = false; });
    }
  };

  const getTesterVariantIds = () => {
    const modal = document.getElementById('miniature-select-modal');
    if (!modal) return new Set();
    return new Set(
      [...modal.querySelectorAll('.msm__card')]
        .map(c => Number(c.dataset.variantId))
        .filter(Boolean)
    );
  };

  const getTesterItems = (items) => {
    const ids = getTesterVariantIds();
    return ids.size ? items.filter(i => ids.has(i.variant_id)) : [];
  };

  const showToast = (message) => {
    let toast = document.getElementById('tester-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'tester-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 5000);
  };

  const removeExcessTesters = async (testerItems, maxAllowed) => {
    _isRemoving = true;
    const trigger = document.getElementById('miniature-select-trigger');
    document.querySelectorAll('button[name="checkout"]').forEach(btn => { btn.disabled = true; });
    try {
      for (const item of testerItems.slice(maxAllowed)) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }
      const cartItemsEl = document.querySelector('cart-items');
      if (cartItemsEl && typeof cartItemsEl.refreshCartSections === 'function') {
        await cartItemsEl.refreshCartSections();
      } else if (typeof refreshCartSectionsGlobal === 'function') {
        await refreshCartSectionsGlobal();
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'tester-gift' } }));
    } catch {
      // silent
    } finally {
      _isRemoving = false;
      if (trigger) { trigger.disabled = false; trigger.classList.remove('loading'); }
      document.querySelectorAll('.cm-bar__btn').forEach(btn => { btn.disabled = false; btn.classList.remove('loading'); });
      document.querySelectorAll('.cm-bar-mobile-wrap .cm-bar').forEach(bar => { bar.classList.remove('loading'); });
      document.querySelectorAll('button[name="checkout"]').forEach(btn => { btn.disabled = false; });
    }
  };

  const fetchMissingTags = async (items, tagMap) => {
    const unknown = items.filter(i => !tagMap[i.product_id] && i.handle);
    if (!unknown.length) return;
    await Promise.all(unknown.map(async item => {
      try {
        const prod = await fetch(`/products/${item.handle}.js`).then(r => r.json());
        tagMap[item.product_id] = prod.tags || [];
      } catch { /* silent */ }
    }));
  };

  const recalculate = async () => {
    if (_recalculating) return;
    _recalculating = true;
    const thresholds = getThresholds();
    try {
      const cart       = await fetch('/cart.js').then(r => r.json());
      const tagMap     = getTagMap();
      await fetchMissingTags(cart.items, tagMap);
      const qualifying = calcQualifyingSubtotal(cart.items, tagMap);
      const shipping   = calcShippingSubtotal(cart.items, tagMap);
      const tier       = calcTier(qualifying, thresholds);
      const testerItems = getTesterItems(cart.items);

      window.__testerGiftCart = cart.items;

      const phase = shipping < thresholds.freeShipping ? 'shipping' : 'tester';
      const barSubtotal = phase === 'shipping' ? shipping : qualifying;
      const noQualifying = hasNoQualifyingItems(cart.items, tagMap);
      const testerCountByTag = cart.items.reduce((s, i) => {
        const tags = tagMap[i.product_id] ?? [];
        return tags.includes('tester-gift') ? s + i.quantity : s;
      }, 0);
      if (window.__lastTesterGiftCount !== testerCountByTag) {
        window.__lastTesterGiftCount = testerCountByTag;
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { tester_gift_count: String(testerCountByTag) } })
        });
      }
      updateProgressBar(barSubtotal, thresholds, tier, testerCountByTag, phase);
      updateShippingStatus(shipping, thresholds);
      updateSelectButtons(tier, testerCountByTag, tier, phase, noQualifying);
      updateModal(tier);

      const count = cart.item_count;
      document.querySelectorAll('.cart-miniature-bar__count').forEach(el => {
        if (_str.itemSingular) el.textContent = `${count} ${count === 1 ? _str.itemSingular : _str.itemPlural}`;
      });

      if (_isRemoving || testerItems.length <= tier) return;

      const excess    = testerItems.length - tier;
      const threshold = tier === 0 ? thresholds.tier1 : thresholds.tier2;
      const msg = _tpl(
        excess === 1 ? _str.toastRemovedOne : _str.toastRemovedOther,
        { symbol: thresholds.symbol, amount: threshold / 100, count: excess }
      );

      showToast(msg);
      const _trigger = document.getElementById('miniature-select-trigger');
      if (_trigger) { _trigger.disabled = true; _trigger.classList.add('loading'); }
      document.querySelectorAll('.cm-bar__btn').forEach(btn => { btn.disabled = true; btn.classList.add('loading'); });
      document.querySelectorAll('.cm-bar-mobile-wrap .cm-bar').forEach(bar => { bar.classList.add('loading'); });
      await removeExcessTesters(testerItems, tier);
    } catch {
      // silent
    } finally {
      _recalculating = false;
      document.querySelectorAll('button[name="checkout"]').forEach(btn => { btn.disabled = false; });
    }
  };

  const applyFromCache = () => {
    if (!window.__testerGiftCart) return false;
    const thresholds  = getThresholds();
    const tagMap      = getTagMap();
    const qualifying  = calcQualifyingSubtotal(window.__testerGiftCart, tagMap);
    const shipping    = calcShippingSubtotal(window.__testerGiftCart, tagMap);
    const tier        = calcTier(qualifying, thresholds);
    const testerItems = getTesterItems(window.__testerGiftCart);

    const phase = shipping < thresholds.freeShipping ? 'shipping' : 'tester';
    const barSubtotal = phase === 'shipping' ? shipping : qualifying;
    const noQualifying = hasNoQualifyingItems(window.__testerGiftCart, tagMap);
    const testerCountByTag = window.__testerGiftCart.reduce((s, i) => {
      const tags = tagMap[i.product_id] ?? [];
      return tags.includes('tester-gift') ? s + i.quantity : s;
    }, 0);
    updateProgressBar(barSubtotal, thresholds, tier, testerCountByTag, phase);
    updateShippingStatus(shipping, thresholds);
    updateSelectButtons(tier, testerCountByTag, tier, phase, noQualifying);
    updateModal(tier);

    const count = window.__testerGiftCart.reduce((s, i) => s + i.quantity, 0);
    document.querySelectorAll('.cart-miniature-bar__count').forEach(el => {
      if (_str.itemSingular) el.textContent = `${count} ${count === 1 ? _str.itemSingular : _str.itemPlural}`;
    });
    return true;
  };

  document.addEventListener('cart:updated', (e) => {
    if (e?.detail?.source === 'dr-gift' || e?.detail?.source === 'tester-gift') return;
    clearTimeout(_debounce);

    // Block checkout immediately — recalculate() re-enables in its finally.
    document.querySelectorAll('button[name="checkout"]').forEach(btn => { btn.disabled = true; });

    try {
      const cartRaw = e?.detail?.cart;
      const parsed  = typeof cartRaw === 'string' ? JSON.parse(cartRaw) : cartRaw;
      if (parsed && Array.isArray(parsed.items)) {
        window.__testerGiftCart = parsed.items;
        applyFromCache();
        _debounce = setTimeout(recalculate, 300);
        return;
      }
    } catch { /* fall through */ }
    recalculate();
  });

  document.addEventListener('cartdrawer:opened', () => {
    applyFromCache();
    recalculate();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', recalculate);
  } else {
    recalculate();
  }

  // Block checkout the moment a quantity button or remove button is clicked in
  // the cart — before the AJAX fires. recalculate() re-enables in its finally.
  document.addEventListener('click', (e) => {
    const isQtyButton = e.target.closest('quantity-input button, .quantity__button, [data-cart-remove]');
    if (!isQtyButton) return;
    if (!isQtyButton.closest('cart-items, #CartDrawer, form#cart, .cart__items')) return;
    document.querySelectorAll('button[name="checkout"]').forEach(btn => { btn.disabled = true; });
  });

  // Safety net: if removal is already in progress when the user clicks checkout
  // (section re-render created a new enabled button mid-removal), wait for it.
  document.addEventListener('click', (e) => {
    if (!_isRemoving) return;
    const btn = e.target.closest('button[name="checkout"]');
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    btn.disabled = true;
    const poll = setInterval(() => {
      if (_isRemoving) return;
      clearInterval(poll);
      const root = window.Shopify?.routes?.root ?? '/';
      window.location.href = root.replace(/\/$/, '') + '/checkout';
    }, 50);
  }, true);

  // If cart.js AJAX fails, cart:updated never fires and recalculate never runs.
  // Re-enable checkout so the user isn't permanently blocked.
  document.addEventListener('cart-error', () => {
    _recalculating = false;
    document.querySelectorAll('button[name="checkout"]').forEach(btn => { btn.disabled = false; });
  });
})();
